import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";
import { isChunkLoadError } from "../../../utils/functions/lazyWithRetry";
import { trackErrorShown } from "../../../utils/functions/analytics";

// Rendered by the router's errorElement when any route inside throws during
// render. The cart in localStorage is the most common culprit (a stale or
// partially-shaped item makes a downstream component blow up before it can
// guard against the missing field), and the user can otherwise be locked out
// across logins because the bad cart is restored from localStorage on every
// page load. The "Reset cart" button gives non-technical users a one-click
// escape hatch without needing DevTools.
function clearCartStorage() {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && (key.endsWith("_products") || key.endsWith("_activeOrder"))) {
      localStorage.removeItem(key);
    }
  }
}

export function RouteErrorView() {
  const { t } = useTranslation();
  const [isResetting, setIsResetting] = useState(false);
  const error = useRouteError();

  // A chunk-load failure is a stale-deploy problem, not a cart problem: the
  // tab's index.html points at hashed chunk files a deploy has replaced.
  // The cart-clearing reset flow below must never run for it — a plain
  // reload (fresh index.html → fresh chunk URLs) is the correct fix.
  // (lazyWithRetry has normally already auto-reloaded once; this view shows
  // when that attempt was spent, so the button is user-driven — no loop.)
  const chunkError = isChunkLoadError(error);

  // Clarity event fejl_vist (flag REACT_APP_CLARITY_EVENTS_ENABLED): a route
  // rendered its error view. Code "chunk" for a stale deploy, "route"
  // otherwise - never the error text. Once per mount: StrictMode runs the
  // effect twice on dev, the ref keeps the count at one.
  const errorReported = useRef(false);
  useEffect(() => {
    if (errorReported.current) return;
    errorReported.current = true;
    trackErrorShown(chunkError ? "chunk" : "route");
  }, [chunkError]);

  function handleReset() {
    setIsResetting(true);
    if (chunkError) {
      window.location.reload();
      return;
    }
    clearCartStorage();
    window.location.href = "/";
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100vw",
        gap: 16,
        padding: 16,
        textAlign: "center",
      }}
    >
      <div>{t("compMisc.errorLoadingPage")}</div>
      <div style={{ fontSize: 13, color: "#555", maxWidth: 480 }}>
        {t("compMisc.tryRefreshBelow")}
      </div>
      <button
        onClick={handleReset}
        disabled={isResetting}
        style={{
          padding: "8px 16px",
          borderRadius: 6,
          border: "1px solid #888",
          background: isResetting ? "#eee" : "#fff",
          cursor: isResetting ? "default" : "pointer",
          fontWeight: 600,
        }}
      >
        {isResetting ? t("compMisc.refreshing") : t("compMisc.refreshPage")}
      </button>
    </div>
  );
}
